import { useEffect, useState } from 'react';
import { CheckCircle2, Download, Eye, FileStack, FileText, ShieldCheck } from 'lucide-react';
import {
  Alert, Button, Card, Dialog, IconAction, InfoTip, RowActions, StatusBadge, TextSkeleton, useIdempotencyKey,
} from '@dru/ui';
import { api } from '../lib/api';
import { useQuery } from '../lib/useQuery';
import { errorMessage } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import { openReport } from '../consoles/lab/routes/Reports';
import type { DocumentGrant } from '@dru/types';

/** One document in a download-all bundle: included, or left out with the reason. */
interface BundleItem { id: string; profileName: string; reportNumber: string; revision: number; included: boolean; reason?: string }
type BundleGrant = DocumentGrant & { items?: BundleItem[] };

/** One report profile on an examination, as the API lists it. */
export interface ProfileReportRow {
  profileCode: string;
  name: string;
  kind: string;
  standalone: boolean;
  status: 'NOT_READY' | 'DRAFT' | 'REVIEW_PENDING' | 'FINAL';
  contentSha256: string;
  missing: string[];
  stale: boolean;
  results: number;
  performedBy: Array<{ name: string; at: string }>;
  verifiedBy: { name: string; at: string } | null;
  reviewedBy: { name: string; at: string } | null;
  canVerify: boolean;
  current: (Issued & { amendmentReason?: string | null }) | null;
  history: Array<Issued & {
    status: string;
    /** What took its place: the physician-reviewed copy of the same revision, or a new revision. */
    replacedBy?: { revision: number; stage: string; issuedAt: string; reason: string | null } | null;
  }>;
}

/** One issued document: its number, revision, stage and — separately — whether its PDF exists. */
interface Issued {
  id: string; reportNumber: string; revision: number; stage: string; issuedAt: string;
  /** PENDING = not rendered yet (made on first download); ISSUED = ready; GENERATION_FAILED = retry. */
  generationStatus?: 'PENDING' | 'ISSUED' | 'GENERATION_FAILED';
  documentGeneratedAt?: string | null;
  generationError?: string | null;
}

const STATUS: Record<ProfileReportRow['status'], { label: string; tone: 'neutral' | 'warning' | 'covered' | 'info' }> = {
  NOT_READY: { label: 'Awaiting results', tone: 'neutral' },
  DRAFT: { label: 'Draft — ready for verification', tone: 'warning' },
  REVIEW_PENDING: { label: 'Verified · physician review pending', tone: 'info' },
  FINAL: { label: 'Signed — final', tone: 'covered' },
};

/** The clinical status in plain words, from the backend's own states. */
function statusWord(r: ProfileReportRow): string {
  if (r.status === 'DRAFT' && r.missing.length) return 'Draft — results still to record';
  if (r.status === 'REVIEW_PENDING' && r.kind === 'LAB') return 'Laboratory authorised · physician review pending';
  return STATUS[r.status].label;
}

/** Which copy this is, the way lab reports label revisions. */
const copyLabel = (stage: string) => (stage === 'FINAL' ? 'Signed' : 'Current working');

/** An earlier row's label: the same revision replaced by its signed copy, or an older revision. */
function supersededLine(h: ProfileReportRow['history'][number]): string {
  const when = h.replacedBy ? ` on ${formatDateTime(h.replacedBy.issuedAt)}` : '';
  if (h.status === 'SUPERSEDED') return `Superseded by the physician-reviewed copy${when}`;
  const reason = h.replacedBy?.reason ? ` — ${h.replacedBy.reason}` : '';
  return h.replacedBy ? `Superseded by revision ${h.replacedBy.revision}${when}${reason}` : 'Superseded';
}

/**
 * Where this list is read from. Each audience has its own server routes, and
 * the server decides what each may see and do — this component only shows
 * what came back.
 */
export type ProfileReportAudience =
  | { kind: 'venue'; encounterId: string; canDownload: boolean }
  | { kind: 'physician'; orderId: string; canReview: boolean };

function paths(a: ProfileReportAudience) {
  if (a.kind === 'venue') {
    const b = `/profile-reports/encounters/${a.encounterId}`;
    return {
      list: b, preview: (c: string) => `${b}/profiles/${encodeURIComponent(c)}/preview`,
      download: (id: string) => `/profile-reports/reports/${id}/download`, all: `${b}/download-all`,
      verify: (c: string) => `${b}/profiles/${encodeURIComponent(c)}/verify`,
    };
  }
  const b = `/physician/examinations/${a.orderId}/profile-reports`;
  return {
    list: b, preview: (c: string) => `${b}/profiles/${encodeURIComponent(c)}/preview`,
    download: (id: string) => `${b}/${id}/download`, all: `${b}/download-all`, review: `${b}/review`,
  };
}

/** Shows a base64 PDF the API returned, in an iframe the person reads before signing. */
function usePdfUrl(base64: string | null) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!base64) { setUrl(null); return; }
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    const u = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [base64]);
  return url;
}

function PreviewDialog({ title, path, onClose, children }: {
  title: string; path: string; onClose: () => void;
  children?: (contentSha256: string) => React.ReactNode;
}) {
  const [doc, setDoc] = useState<{ base64: string; contentSha256: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.get<{ base64: string; contentSha256: string }>(path).then(setDoc).catch((e) => setError(errorMessage(e)));
  }, [path]);
  const url = usePdfUrl(doc?.base64 ?? null);
  return (
    <Dialog title={title} wide onClose={onClose}>
      {error && <Alert tone="danger">{error}</Alert>}
      {!doc && !error && <TextSkeleton lines={6} />}
      {url && <iframe title={`${title} — PDF`} src={url} className="h-[65vh] w-full rounded-input border border-neutral-200" />}
      {doc && children?.(doc.contentSha256)}
    </Dialog>
  );
}

export function ProfileReports({ audience }: { audience: ProfileReportAudience }) {
  const p = paths(audience);
  const list = useQuery<ProfileReportRow[]>(() => api.get(p.list), [p.list]);
  const [busy, setBusy] = useState<string | null>(null);
  const [bundle, setBundle] = useState<BundleItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ProfileReportRow | null>(null);
  const [verifying, setVerifying] = useState<ProfileReportRow | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [failed, setFailed] = useState<Record<string, string>>({});
  const idem = useIdempotencyKey();

  /**
   * View or download one document. Profile reports are rendered on first
   * download, so the row reads "Generating…" while the server makes the PDF,
   * and the list is re-read afterwards so its PDF state is the server's.
   */
  async function fetchDoc(id: string, mode: 'view' | 'download') {
    setBusy(`dl-${id}`); setError(null);
    setFailed((f) => { const n = { ...f }; delete n[id]; return n; });
    try { await openReport(p.download(id), mode); }
    catch (e) { setFailed((f) => ({ ...f, [id]: errorMessage(e) })); }
    finally { setBusy(null); list.reload(); }
  }

  // Only profiles ordered on this examination (the server lists those), and
  // only once something was recorded or is owed — never placeholders.
  const rows = (list.data ?? []).filter((r) => r.results > 0 || r.missing.length);
  const issued = rows.filter((r) => r.current);
  const notIssued = rows.filter((r) => !r.current);
  const canDownload = audience.kind === 'venue' ? audience.canDownload : true;
  const reviewable = audience.kind === 'physician' && audience.canReview
    ? rows.filter((r) => r.status === 'REVIEW_PENDING') : [];

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key); setError(null);
    try { await fn(); } catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  }

  async function verify(row: ProfileReportRow, contentSha256: string) {
    const verifyPath = 'verify' in p ? p.verify : undefined;
    if (!verifyPath) return;
    await run(`verify-${row.profileCode}`, async () => {
      await api.post(verifyPath(row.profileCode), { contentSha256 }, idem.key());
      idem.rotate(); setVerifying(null); list.reload();
    });
  }

  async function review(items: Array<{ profileCode: string; contentSha256: string }>) {
    const reviewPath = 'review' in p ? p.review : undefined;
    if (!reviewPath) return;
    await run('review', async () => {
      await api.post(reviewPath, { items }, idem.key());
      idem.rotate(); setReviewing(false); list.reload();
    });
  }

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-4 py-3">
        <h2 className="flex items-center gap-1.5 text-h4 text-neutral-900">
          Medical reports
          <InfoTip label="About these reports">
            One document per report profile — CBC, lipid profile, ECG and so on — printed only for tests that were ordered
            and performed. Each shows who performed it, who verified it and the physician's review, and only once each of
            those acts has happened.
          </InfoTip>
        </h2>
        <div className="flex flex-wrap gap-2">
          {reviewable.length > 0 && (
            <Button size="sm" onClick={() => setReviewing(true)} icon={<ShieldCheck className="h-4 w-4" aria-hidden />}>
              Review & sign ({reviewable.length})
            </Button>
          )}
          {canDownload && issued.length > 1 && (
            <Button size="sm" variant="secondary" loading={busy === 'all'} icon={<FileStack className="h-4 w-4" aria-hidden />}
              onClick={() => run('all', async () => {
                const g = await openReport<BundleGrant>(p.all, 'download');
                setBundle(g.items ?? null);
              })}>
              Download all ({issued.length})
            </Button>
          )}
        </div>
      </div>
      {error && <Alert tone="danger" className="mx-4 mt-3">{error}</Alert>}
      {canDownload && issued.length > 1 && notIssued.length > 0 && (
        <p className="mx-4 mt-3 text-caption text-neutral-600">
          “Download all” holds the {issued.length} issued report{issued.length === 1 ? '' : 's'}, current revisions only.
          Not issued yet: {notIssued.map((r) => r.name).join(', ')}.
        </p>
      )}
      {/* What the downloaded PDF holds, revision by revision — current
          revisions only — and anything left out, with why. */}
      {bundle && (
        <div className="mx-4 mt-3 rounded-input border border-neutral-200 bg-neutral-50 px-3 py-2 text-caption text-neutral-700" role="status">
          <span className="font-semibold text-neutral-900">In the PDF: </span>
          {bundle.filter((b) => b.included).map((b) => `${b.profileName} (rev ${b.revision})`).join(', ') || 'nothing'}.
          {bundle.some((b) => !b.included) && (
            <span className="mt-1 flex flex-wrap items-center gap-2 text-warning-600">
              <span className="min-w-0">Left out: {bundle.filter((b) => !b.included).map((b) => `${b.profileName} — ${b.reason ?? 'not available'}`).join('; ')}.</span>
              {/* Only the ones that failed, as their own PDF; what already
                  downloaded is not fetched again. */}
              <Button size="sm" variant="secondary" loading={busy === 'retry'}
                onClick={() => run('retry', async () => {
                  const failed = bundle.filter((b) => !b.included).map((b) => b.id);
                  const g = await openReport<BundleGrant>(`${p.all}?ids=${failed.join(',')}`, 'download');
                  const again = new Map((g.items ?? []).map((i) => [i.id, i]));
                  setBundle((prev) => (prev ?? []).map((b) => again.get(b.id) ?? b));
                })}>
                Retry failed ({bundle.filter((b) => !b.included).length})
              </Button>
            </span>
          )}
        </div>
      )}
      {list.loading && !list.data && <div className="p-4"><TextSkeleton lines={4} /></div>}
      {list.error && <Alert tone="danger" className="mx-4 my-3">{list.error}</Alert>}
      {list.data && rows.length === 0 && (
        <p className="px-4 py-4 text-small text-neutral-600">No results have been recorded for this examination yet.</p>
      )}
      <ul className="divide-y divide-neutral-200">
        {rows.map((r) => {
          const c = r.current;
          const gen = c ? (busy === `dl-${c.id}` ? 'GENERATING' : failed[c.id] ? 'GENERATION_FAILED' : c.generationStatus ?? 'PENDING') : null;
          return (
          <li key={r.profileCode} className="px-4 py-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-small font-semibold text-neutral-900">
                <FileText aria-hidden className="h-4 w-4 text-neutral-400" />
                {r.name}
                {r.standalone && <span className="text-caption font-normal text-neutral-500">ordered individually</span>}
                <StatusBadge tone={STATUS[r.status].tone}>{statusWord(r)}</StatusBadge>
                {r.stale && <StatusBadge tone="warning">Results changed — re-verification needed</StatusBadge>}
              </div>
              <div className="mt-1 text-caption text-neutral-600">
                {c
                  ? <>
                      <span className="font-medium text-neutral-800">{c.reportNumber} · revision {c.revision}</span>
                      {' · '}{copyLabel(c.stage)}{' · issued '}{formatDateTime(c.issuedAt)}
                      {c.amendmentReason && <> · amended: {c.amendmentReason}</>}
                    </>
                  : 'Not issued yet'}
                {r.missing.length > 0 && <> · still to record: {r.missing.join(', ')}</>}
              </div>
              <div className="mt-0.5 text-caption text-neutral-500">
                {r.performedBy.length ? `Performed: ${r.performedBy.map((x) => x.name).join(', ')}` : 'Performed: —'}
                {' · '}{r.verifiedBy ? `Verified: ${r.verifiedBy.name}` : 'Verification pending'}
                {' · '}{r.reviewedBy ? `Reviewed: ${r.reviewedBy.name}, ${formatDateTime(r.reviewedBy.at)}` : 'Physician review pending'}
              </div>
              {/* The PDF's own state, never inferred from the clinical one. */}
              {c && canDownload && (
                <div className="mt-1 flex flex-wrap items-center gap-2 text-caption" aria-live="polite">
                  {gen === 'GENERATING' && <StatusBadge tone="pending">PDF generating…</StatusBadge>}
                  {gen === 'ISSUED' && <span className="text-neutral-600">PDF ready{c.documentGeneratedAt ? ` · made ${formatDateTime(c.documentGeneratedAt)}` : ''}</span>}
                  {gen === 'PENDING' && <span className="text-neutral-500">PDF not made yet — it is generated when first viewed or downloaded</span>}
                  {gen === 'GENERATION_FAILED' && (
                    <>
                      <StatusBadge tone="overdue">PDF failed</StatusBadge>
                      <span className="text-danger-600">{failed[c.id] ?? c.generationError ?? 'The document could not be generated.'}</span>
                      <Button size="sm" variant="secondary" onClick={() => void fetchDoc(c.id, 'download')}>Retry</Button>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {r.canVerify && (
                <Button size="sm" variant="secondary" onClick={() => setVerifying(r)}>Verify…</Button>
              )}
              <RowActions>
                {r.results > 0 && !c && (
                  <IconAction label={`Preview ${r.name} (draft)`} icon={Eye} onClick={() => setPreview(r)} />
                )}
                {c && canDownload && (
                  <>
                    <IconAction label={`View ${r.name} revision ${c.revision}`} icon={Eye} disabled={gen === 'GENERATING'}
                      onClick={() => void fetchDoc(c.id, 'view')} />
                    <IconAction label={`Download ${r.name} revision ${c.revision} (PDF)`} icon={Download} loading={gen === 'GENERATING'}
                      onClick={() => void fetchDoc(c.id, 'download')} />
                  </>
                )}
                {c && r.results > 0 && (
                  <IconAction label={`Preview ${r.name} as it reads now (draft)`} icon={FileText} onClick={() => setPreview(r)} />
                )}
              </RowActions>
            </div>
            </div>
            {/* Every earlier copy, never truncated; each still opens when permitted. */}
            {r.history.length > 0 && (
              <details className="mt-2 rounded-input border border-neutral-200 bg-neutral-50 px-3 py-1.5">
                <summary className="cursor-pointer text-caption font-medium text-neutral-700 max-md:min-h-10 max-md:py-2">
                  Earlier revisions ({r.history.length})
                </summary>
                <ul className="mt-1 divide-y divide-neutral-200">
                  {r.history.map((h) => (
                    <li key={h.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                      <div className="min-w-0 text-caption">
                        <span className="font-medium text-neutral-800">{h.reportNumber} · revision {h.revision}</span>
                        <StatusBadge tone="neutral" className="ml-2">Superseded</StatusBadge>
                        <span className="ml-2 text-neutral-500">issued {formatDateTime(h.issuedAt)} · {copyLabel(h.stage).toLowerCase()} copy</span>
                        <div className="text-neutral-600">{supersededLine(h)}</div>
                        {failed[h.id] && <div className="text-danger-600">{failed[h.id]}</div>}
                      </div>
                      {canDownload && (
                        <RowActions>
                          <IconAction label={`View superseded ${h.reportNumber} revision ${h.revision}`} icon={Eye}
                            disabled={busy === `dl-${h.id}`} onClick={() => void fetchDoc(h.id, 'view')} />
                          <IconAction label={`Download superseded ${h.reportNumber} revision ${h.revision}`} icon={Download}
                            loading={busy === `dl-${h.id}`} onClick={() => void fetchDoc(h.id, 'download')} />
                        </RowActions>
                      )}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </li>
          );
        })}
      </ul>

      {preview && (
        <PreviewDialog title={`${preview.name} — current preview`} path={p.preview(preview.profileCode)} onClose={() => setPreview(null)}>
          {() => <p className="mt-2 text-caption text-neutral-500">A live preview of the results as they stand, marked as a draft. The issued document is the one to download.</p>}
        </PreviewDialog>
      )}

      {verifying && (
        <PreviewDialog title={`Verify ${verifying.name}`} path={p.preview(verifying.profileCode)} onClose={() => { if (!busy) setVerifying(null); }}>
          {(sha) => (
            <div className="mt-3 space-y-2">
              <p className="text-small text-neutral-700">
                You are verifying exactly the results shown above. If any of them changes before you confirm, the
                verification is refused and you will be asked to look again.
              </p>
              <Button loading={busy === `verify-${verifying.profileCode}`} onClick={() => void verify(verifying, sha)}
                icon={<CheckCircle2 className="h-4 w-4" aria-hidden />}>
                Verify these results
              </Button>
            </div>
          )}
        </PreviewDialog>
      )}

      {reviewing && (
        <ReviewDialog rows={reviewable} previewPath={p.preview} busy={busy === 'review'} onClose={() => setReviewing(false)}
          onConfirm={(items) => void review(items)} />
      )}
    </Card>
  );
}

/** The physician reads each report before signing; only those opened can be signed. */
function ReviewDialog({ rows, previewPath, busy, onClose, onConfirm }: {
  rows: ProfileReportRow[]; previewPath: (code: string) => string; busy: boolean;
  onClose: () => void; onConfirm: (items: Array<{ profileCode: string; contentSha256: string }>) => void;
}) {
  const [seen, setSeen] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<ProfileReportRow | null>(rows[0] ?? null);
  return (
    <Dialog title="Review and sign reports" wide onClose={() => { if (!busy) onClose(); }}>
      <div className="grid gap-3 md:grid-cols-[220px_1fr]">
        <ul className="space-y-1">
          {rows.map((r) => (
            <li key={r.profileCode}>
              <button type="button" onClick={() => setOpen(r)}
                className={`flex w-full items-center justify-between rounded-btn px-2.5 py-2 text-left text-small ${open?.profileCode === r.profileCode ? 'bg-primary-50 text-primary-800' : 'hover:bg-neutral-100'}`}>
                <span>{r.name}</span>
                {seen[r.profileCode] && <CheckCircle2 aria-label="Read" className="h-4 w-4 text-success-600" />}
              </button>
            </li>
          ))}
        </ul>
        <div>
          {open && <ReviewPane key={open.profileCode} path={previewPath(open.profileCode)}
            onRead={(sha) => setSeen((s) => ({ ...s, [open.profileCode]: sha }))} />}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-neutral-200 pt-3">
        <p className="text-caption text-neutral-600">
          {Object.keys(seen).length} of {rows.length} opened. Only the reports you have opened are signed, on the exact
          content you saw.
        </p>
        <Button loading={busy} disabled={!Object.keys(seen).length}
          onClick={() => onConfirm(Object.entries(seen).map(([profileCode, contentSha256]) => ({ profileCode, contentSha256 })))}
          icon={<ShieldCheck className="h-4 w-4" aria-hidden />}>
          Sign {Object.keys(seen).length || ''} reviewed report{Object.keys(seen).length === 1 ? '' : 's'}
        </Button>
      </div>
    </Dialog>
  );
}

function ReviewPane({ path, onRead }: { path: string; onRead: (sha: string) => void }) {
  const [doc, setDoc] = useState<{ base64: string; contentSha256: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.get<{ base64: string; contentSha256: string }>(path)
      .then((d) => { setDoc(d); onRead(d.contentSha256); })
      .catch((e) => setError(errorMessage(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
  const url = usePdfUrl(doc?.base64 ?? null);
  if (error) return <Alert tone="danger">{error}</Alert>;
  if (!url) return <TextSkeleton lines={6} />;
  return <iframe title="Report preview" src={url} className="h-[60vh] w-full rounded-input border border-neutral-200" />;
}
